import { useState, type FormEvent } from 'react';
import { LogIn, Eye, EyeOff, Key, AlertCircle, Loader2, Compass, ShieldCheck } from 'lucide-react';
import { motion } from 'motion/react';
import type { Settings } from '../types';
import { CollectClient } from '../lib/api';
import { customServerOf, saveSettings, selfHostedServerOffered, serverUrlFromInput } from '../lib/settings';
import { templateLabel } from '../lib/templates';
import { isDevBuild, startLogin, type TrustConfig } from '../lib/oidc';
import { AdvancedServer } from './AdvancedServer';

/**
 * /login — entry for links from the HIVE Portal (?from=portal) and the HIVE
 * runes (?from=runes&template=<slug>).
 *
 * Primary: "Sign in with Ordinal Pass" — OIDC authorization code + PKCE at
 * Qodoonect Trust (lib/oidc.ts), on when VITE_TRUST_ISSUER and
 * VITE_TRUST_CLIENT_ID are set. Fallback (closed beta, on unless
 * VITE_ACCESS_KEY_LOGIN=off): an access key issued by Qodoonect, checked
 * against the server before it is saved. The fallback stays until the HIVE
 * backend accepts Trust tokens with workspace isolation (design:
 * collect-login/design.md, S-1).
 *
 * The server is this site (serverUrl SAME_ORIGIN — /api/collect/* is proxied by
 * the hosting), so the form asks only for the key; a self-hosted server goes
 * under "Advanced" (AdvancedServer), empty and collapsed by default, and only in
 * builds that can reach one (selfHostedServerOffered: not the hosted site).
 */
export function LoginPanel({
  initial,
  from,
  template,
  handoff = false,
  trust,
  keyLogin,
  notice,
  onSignedIn,
}: {
  initial: Settings;
  from?: string;
  template?: string;
  /** A rune handoff is waiting (route.ts handoff): the Ordinal Pass round trip comes back to it. */
  handoff?: boolean;
  trust: TrustConfig | null;
  keyLogin: boolean;
  /** Message carried over from a failed /auth/callback. */
  notice?: string | null;
  onSignedIn: (s: Settings) => void;
}) {
  const [offerServer] = useState(() => selfHostedServerOffered(initial));
  const [customServer, setCustomServer] = useState(() => customServerOf(initial));
  const [advancedOpen, setAdvancedOpen] = useState(() => Boolean(customServerOf(initial)));
  const [apiKey, setApiKey] = useState(initial.apiKey);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [passBusy, setPassBusy] = useState(false);
  const [error, setError] = useState<string | null>(notice || null);
  const [showKeyForm, setShowKeyForm] = useState(!trust);

  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    const key = apiKey.trim();
    if (!key) {
      setError('Paste your access key first.');
      return;
    }
    const server = serverUrlFromInput(customServer);
    if (!server.ok) {
      setAdvancedOpen(true);
      setError(server.message);
      return;
    }
    const s: Settings = { serverUrl: server.serverUrl, apiKey: key };
    setBusy(true);
    setError(null);
    try {
      const r = await new CollectClient(s).testConnection('login');
      if (!r.ok) {
        setError(r.message);
        return;
      }
      saveSettings(s);
      onSignedIn(s);
    } finally {
      setBusy(false);
    }
  };

  const passSignIn = async () => {
    if (!trust) return;
    setPassBusy(true);
    setError(null);
    try {
      await startLogin(trust, { from, template, handoff });
    } catch {
      setPassBusy(false);
      setError('Could not start the sign-in. Reload the page and try again.');
    }
  };

  // Flag off: production builds show no Ordinal Pass button at all; dev builds
  // show it disabled so the switch is visible while working on it.
  const passPanel = !trust && !isDevBuild() ? null : (
    <div className={`glass-panel-strong rounded-2xl p-6 ${trust ? 'mb-4' : 'mt-4'}`}>
      <button
        type="button"
        className="btn btn-primary w-full justify-center"
        onClick={passSignIn}
        disabled={!trust || passBusy}
        style={!trust ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
      >
        {passBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
        {passBusy ? 'Opening Qodoonect Trust…' : 'Sign in with Ordinal Pass'}
      </button>
      <p className="text-xs mt-3 text-center" style={{ color: 'rgba(240,253,244,0.65)' }}>
        {trust
          ? 'Collect receives a short-lived token for your workspace. Your password stays with Trust.'
          : 'Sign-in with Ordinal Pass is not switched on for this site yet.'}
      </p>
    </div>
  );

  const keyToggle = keyLogin && trust && !showKeyForm ? (
    <p className="text-xs text-center mb-4">
      <button type="button" className="underline opacity-60 hover:opacity-100" onClick={() => setShowKeyForm(true)}>
        Use an access key instead (closed beta)
      </button>
    </p>
  ) : null;

  const keyForm = keyLogin && showKeyForm ? (
    <form onSubmit={signIn} className="glass-panel-strong rounded-2xl p-6">
      <label className="label flex items-center gap-2 mb-3" htmlFor="login-key">
        <Key size={12} />
        Access key
      </label>
      <div className="relative mb-2">
        <input
          id="login-key"
          className="field pr-12"
          type={showKey ? 'text' : 'password'}
          placeholder="paste your access key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          autoFocus={!trust}
        />
        <button
          type="button"
          onClick={() => setShowKey(!showKey)}
          className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
          title={showKey ? 'Hide key' : 'Show key'}
        >
          {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      <p className="text-xs mb-5" style={{ color: 'rgba(240,253,244,0.65)' }}>
        The access key Qodoonect gave you. It is kept in this browser and sent only to the Collect server.
      </p>

      {offerServer && (
        <AdvancedServer
          id="login-server"
          className="mb-5"
          value={customServer}
          onChange={setCustomServer}
          open={advancedOpen}
          onOpenChange={setAdvancedOpen}
        />
      )}

      <button type="submit" className={`btn ${trust ? 'btn-ghost' : 'btn-primary'} w-full justify-center`} disabled={busy}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
        {busy ? 'Checking key…' : trust ? 'Sign in with access key' : 'Sign in'}
      </button>
    </form>
  ) : null;

  return (
    <motion.div
      className="max-w-xl mx-auto"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <div className="mb-8 text-center">
        <div className="w-14 h-14 rounded-xl mx-auto mb-5 flex items-center justify-center heartbeat" style={{
          background: 'linear-gradient(135deg, #10B981, #064E3B)',
        }}>
          <LogIn size={26} style={{ color: '#F0FDF4' }} />
        </div>
        <h1 className="display text-5xl mb-3">Sign in to Collect</h1>
        <p className="text-base" style={{ color: 'rgba(240,253,244,0.72)' }}>
          {trust
            ? 'Use your Ordinal Pass. You sign in at Qodoonect Trust and come straight back here.'
            : 'Collect is in closed beta. Sign in with the access key Qodoonect gave you.'}
        </p>
      </div>

      {/* Where the visitor came from */}
      {(from || template) && (
        <div className="glass-panel rounded-xl p-4 mb-4 flex items-start gap-3">
          <Compass size={16} className="flex-none mt-0.5" style={{ color: '#34D399' }} />
          <div className="text-sm" style={{ color: 'rgba(240,253,244,0.7)' }}>
            {from && <div>Opened from <span className="font-mono" style={{ color: '#34D399' }}>{fromLabel(from)}</span>.</div>}
            {template && (
              <div>
                Template <span className="font-mono" style={{ color: '#34D399' }}>{templateLabel(template)}</span> is
                selected in the send form after you sign in.
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl p-3 mb-4 flex items-start gap-3" style={{ border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)' }} role="alert">
          <AlertCircle size={16} style={{ color: '#FBBF24' }} className="flex-none mt-0.5" />
          <div className="text-sm" style={{ color: '#FDE68A' }}>{error}</div>
        </div>
      )}

      {trust ? <>{passPanel}{keyToggle}{keyForm}</> : <>{keyForm}{passPanel}</>}

      {keyLogin && (
        <p className="text-xs mt-5 text-center" style={{ color: 'rgba(240,253,244,0.65)' }}>
          No key yet? Ask your Qodoonect contact. Self-serve accounts are not available yet.
        </p>
      )}
    </motion.div>
  );
}

function fromLabel(from: string): string {
  if (from === 'portal') return 'HIVE Portal';
  if (from === 'runes') return 'HIVE runes';
  return from;
}
